import { Team } from './team';
import { Worm } from '../entities/worm';
import { WA_PHYSICS } from '../core/constants';
import { getGameRNG } from '../core/rng';

export type GamePhase =
  | 'setup'
  | 'playing'
  | 'retreat'
  | 'damage_resolution'
  | 'between_turns'
  | 'finished';

export interface GameStateData {
  teams: Team[];
  currentTeamIndex: number;
  turnTimeRemaining: number;
  retreatTimeRemaining: number;
  roundNumber: number;
  wind: number;
  phase: GamePhase;
  winner: Team | null;
}

export interface ModeRuleConfig {
  windModel: 'horizontal' | 'vector360';
  windMax: number;
  turnTime: number;
  retreatTime: number;
  movementBudgetPx: number | null;
  /** GunBound rule: lock walking once the worm has fired this turn. */
  lockMovementAfterFire?: boolean;
  turnOrder: 'roundRobin' | 'delay';
  delayPerSecond: number;
  /** Accumulate/display turn delays even when turn order is round-robin. */
  trackDelays?: boolean;
}

export class GameState {
  teams: Team[] = [];
  currentTeamIndex = 0;
  turnTimeRemaining: number = WA_PHYSICS.TURN.DURATION;
  retreatTimeRemaining: number = WA_PHYSICS.TURN.RETREAT_TIME;
  roundNumber = 1;
  wind = 0;
  /** Vertical wind component (vector360 wind model only). */
  windY = 0;
  phase: GamePhase = 'setup';
  winner: Team | null = null;
  turnSequence = 0;
  /** Horizontal pixels the active worm may still walk this turn. */
  movementRemaining = Infinity;

  private weaponFired = false;
  private shotsFiredThisTurn = 0;
  private windModel: 'horizontal' | 'vector360' = 'horizontal';
  private windMax: number = WA_PHYSICS.WORLD.MAX_WIND;
  private turnDuration: number = WA_PHYSICS.TURN.DURATION;
  private retreatDuration: number = WA_PHYSICS.TURN.RETREAT_TIME;
  private movementBudget: number | null = null;
  private lockMovementAfterFire = false;
  /** Authority's movement-lock verdict, mirrored onto online clients so they
   *  don't predict a move the server will reject. Always false on authority. */
  syncedMovementLock = false;
  private turnOrderKind: 'roundRobin' | 'delay' = 'roundRobin';
  private delayPerSecond = 10;
  private trackDelays = false;
  /** Flat delay for taking a turn at all (delay mode), like GunBound's base delay. */
  private readonly BASE_TURN_DELAY = 500;
  private teamDelays = new Map<string, number>();
  private fifoPosition = new Map<string, number>();
  private queueCounter = 0;
  private pendingTurnDelay = 0;
  private turnsThisRound = 0;
  private turnsUntilWindChange = 0;
  /** Dual item: extra shots that keep the turn going after firing. */
  private extraShotsThisTurn = 0;
  /** Dual item: a duplicate shot is due (consumed by the weapon executor). */
  private dualShotPending = false;
  /** Power-Up/Blood items: damage multiplier consumed by the next shot. */
  private shotDamageMultiplier = 1;
  /** Players may use at most one match item per turn. */
  itemUsedThisTurn = false;

  /** Apply mode rules (call before startGame). Defaults reproduce classic. */
  configureMode(config: ModeRuleConfig): void {
    this.windModel = config.windModel;
    this.windMax = config.windMax;
    this.turnDuration = config.turnTime;
    this.retreatDuration = config.retreatTime;
    this.movementBudget = config.movementBudgetPx;
    this.movementRemaining = this.movementBudget ?? Infinity;
    this.lockMovementAfterFire = config.lockMovementAfterFire ?? false;
    this.turnTimeRemaining = this.turnDuration;
    this.retreatTimeRemaining = this.retreatDuration;
    this.turnOrderKind = config.turnOrder;
    this.delayPerSecond = config.delayPerSecond;
    this.trackDelays = config.trackDelays || config.turnOrder === 'delay';
    this.teamDelays.clear();
    this.fifoPosition.clear();
    this.queueCounter = 0;
    this.pendingTurnDelay = 0;
    this.turnsThisRound = 0;
    this.extraShotsThisTurn = 0;
    this.dualShotPending = false;
    this.shotDamageMultiplier = 1;
    this.itemUsedThisTurn = false;
  }

  // ----- match item effects -----

  /** Dual: the next turn-ending shot repeats automatically. */
  grantExtraShot(): void {
    this.extraShotsThisTurn++;
  }

  /** True exactly once after a shot armed by Dual (executor schedules it). */
  takeDualShotPending(): boolean {
    if (!this.dualShotPending) return false;
    this.dualShotPending = false;
    return true;
  }

  /** Power-Up/Blood: scale the next shot's damage. */
  boostShotDamage(multiplier: number): void {
    this.shotDamageMultiplier *= multiplier;
  }

  /** Damage multiplier for the shot being fired right now (resets to 1). */
  consumeShotDamageBoost(): number {
    const multiplier = this.shotDamageMultiplier;
    this.shotDamageMultiplier = 1;
    return multiplier;
  }

  /** Wind Change item: immediate re-roll, then a fresh hold period. */
  rerollWind(): void {
    this.randomizeWind();
    this.scheduleNextWindChange();
  }

  /** Add action delay (weapon fired, item used) to the current turn's cost. */
  addTurnDelay(amount: number): void {
    if (this.trackDelays && Number.isFinite(amount) && amount > 0) {
      this.pendingTurnDelay += amount;
    }
  }

  /** Accumulated delays per team (null when delays are not tracked). */
  getTeamDelays(): Record<string, number> | null {
    if (!this.trackDelays) return null;
    const out: Record<string, number> = {};
    for (const team of this.teams) {
      out[team.id] = this.teamDelays.get(team.id) ?? 0;
    }
    return out;
  }

  /** Mirror authoritative delays on clients (display only). */
  applyTeamDelays(delays: Record<string, number>): void {
    for (const [teamId, delay] of Object.entries(delays)) {
      this.teamDelays.set(teamId, delay);
    }
  }

  /** Consume movement budget; returns false when exhausted. */
  tryConsumeMovement(px: number): boolean {
    if (this.movementRemaining <= 0) return false;
    this.movementRemaining = Math.max(0, this.movementRemaining - px);
    return true;
  }

  addTeam(team: Team): void {
    this.teams.push(team);
  }

  startGame(): void {
    if (this.teams.length < 2) {
      throw new Error('Need at least 2 teams to start');
    }
    this.phase = 'playing';
    this.currentTeamIndex = 0;
    this.roundNumber = 1;
    this.turnSequence = 1;
    this.randomizeWind();
    this.scheduleNextWindChange();
    this.resetTurnTimer();
    this.movementRemaining = this.effectiveMovementBudget() ?? Infinity;
  }

  getCurrentTeam(): Team | null {
    if (this.teams.length === 0) return null;
    return this.teams[this.currentTeamIndex];
  }

  getCurrentWorm(): Worm | null {
    const team = this.getCurrentTeam();
    return team?.getCurrentWorm() ?? null;
  }

  onWeaponFired(endTurn = true): void {
    this.weaponFired = true;
    this.shotsFiredThisTurn++;

    if (endTurn && this.extraShotsThisTurn > 0) {
      // Dual item: keep the turn alive; the duplicate shot fires
      // automatically (manual refire stays blocked via shotsFiredThisTurn).
      this.extraShotsThisTurn--;
      this.dualShotPending = true;
      return;
    }

    if (endTurn) {
      this.phase = 'retreat';
      this.retreatTimeRemaining = this.retreatDuration;
    }
  }

  onWormTookFallDamage(): void {
    this.onCurrentWormTookDamage();
  }

  onCurrentWormTookDamage(): void {
    if (this.phase === 'playing' || this.phase === 'retreat') {
      this.phase = 'damage_resolution';
    }
  }

  startDamageResolution(): void {
    if (this.phase !== 'finished') {
      this.phase = 'damage_resolution';
    }
  }

  finishDamageResolution(): void {
    if (this.phase === 'damage_resolution') {
      this.phase = 'between_turns';
    }
  }

  getShotsFired(): number {
    return this.shotsFiredThisTurn;
  }

  advanceTurn(): void {
    const currentTeam = this.getCurrentTeam();
    currentTeam?.advanceWorm();

    if (this.trackDelays) {
      this.accrueTurnDelay(currentTeam);
    }
    if (this.turnOrderKind === 'delay') {
      if (!this.pickNextTeamByDelay()) return;
    } else if (!this.pickNextTeamRoundRobin()) {
      return;
    }

    this.turnSequence++;
    if (--this.turnsUntilWindChange <= 0) {
      this.randomizeWind();
      this.scheduleNextWindChange();
    }
    this.resetTurnTimer();
    this.weaponFired = false;
    this.shotsFiredThisTurn = 0;
    this.extraShotsThisTurn = 0;
    this.dualShotPending = false;
    this.shotDamageMultiplier = 1;
    this.itemUsedThisTurn = false;
    this.movementRemaining = this.effectiveMovementBudget() ?? Infinity;
    this.phase = 'playing';
  }

  /** Per-tank movement budget: the current team's tank override (set at setup)
   *  takes precedence over the mode-wide budget. undefined override => mode. */
  private effectiveMovementBudget(): number | null {
    const team = this.getCurrentTeam();
    if (team && team.movementBudgetPx !== undefined) return team.movementBudgetPx;
    return this.movementBudget;
  }

  private pickNextTeamRoundRobin(): boolean {
    const startIndex = this.currentTeamIndex;
    let loopCount = 0;
    do {
      this.currentTeamIndex = (this.currentTeamIndex + 1) % this.teams.length;
      loopCount++;
      if (loopCount > this.teams.length) {
        this.checkGameOver();
        return false;
      }
    } while (this.getCurrentTeam()?.isEliminated() && !this.checkGameOver());

    if (this.currentTeamIndex <= startIndex) {
      this.roundNumber++;
    }
    return true;
  }

  /** Charge the finished turn's delay cost to the team that took it. */
  private accrueTurnDelay(currentTeam: Team | null): void {
    if (currentTeam) {
      const secondsUsed = Math.max(0, this.turnDuration - this.turnTimeRemaining);
      const cost =
        this.BASE_TURN_DELAY +
        this.pendingTurnDelay +
        Math.round(secondsUsed) * this.delayPerSecond;
      this.teamDelays.set(currentTeam.id, (this.teamDelays.get(currentTeam.id) ?? 0) + cost);
      this.fifoPosition.set(currentTeam.id, ++this.queueCounter);
    }
    this.pendingTurnDelay = 0;
  }

  /**
   * GunBound-style turn order: every turn costs delay (base + seconds spent
   * + weapon/item costs); the alive team with the lowest accumulated delay
   * acts next (FIFO on ties). Fast play can let a team act twice in a row.
   */
  private pickNextTeamByDelay(): boolean {
    if (this.checkGameOver()) return false;
    const alive = this.teams.filter((t) => !t.isEliminated());
    if (alive.length === 0) return false;

    let best = alive[0];
    for (const team of alive) {
      const d = this.teamDelays.get(team.id) ?? 0;
      const bestD = this.teamDelays.get(best.id) ?? 0;
      const f = this.fifoPosition.get(team.id) ?? 0;
      const bestF = this.fifoPosition.get(best.id) ?? 0;
      if (d < bestD || (d === bestD && f < bestF)) {
        best = team;
      }
    }
    this.currentTeamIndex = this.teams.indexOf(best);

    this.turnsThisRound++;
    if (this.turnsThisRound >= alive.length) {
      this.roundNumber++;
      this.turnsThisRound = 0;
    }
    return true;
  }

  updateTimers(deltaTime: number, isAuthority = true, pauseTimer = false): void {
    if (this.phase === 'playing') {
      if (!pauseTimer) {
        this.turnTimeRemaining -= deltaTime;
      }
      if (isAuthority && this.turnTimeRemaining <= 0) {
        this.onTurnTimeout();
      }
    } else if (this.phase === 'retreat') {
      this.retreatTimeRemaining -= deltaTime;
      if (isAuthority && this.retreatTimeRemaining <= 0) {
        this.phase = 'damage_resolution';
      }
    } else if (this.phase === 'between_turns' && isAuthority) {
      this.advanceTurn();
    }
  }

  private onTurnTimeout(): void {
    if (this.weaponFired) {
      this.phase = 'retreat';
      this.retreatTimeRemaining = 0 as number;
    } else {
      this.phase = 'between_turns';
    }
  }

  private resetTurnTimer(): void {
    this.turnTimeRemaining = this.turnDuration;
    this.retreatTimeRemaining = this.retreatDuration;
  }

  private randomizeWind(): void {
    if (this.windModel === 'vector360') {
      // GunBound-style wind: any direction.
      const angle = getGameRNG().nextFloat(0, Math.PI * 2);
      const strength = getGameRNG().nextFloat(0, this.windMax);
      this.wind = Math.round(Math.cos(angle) * strength);
      this.windY = Math.round(Math.sin(angle) * strength);
      return;
    }
    this.wind = getGameRNG().nextInt(-this.windMax, this.windMax);
  }

  /** Wind holds for a random 6 to 12 turns before changing. */
  private scheduleNextWindChange(): void {
    this.turnsUntilWindChange = getGameRNG().nextInt(6, 12);
  }

  checkGameOver(): boolean {
    const aliveTeams = this.teams.filter((t) => !t.isEliminated());
    // Allied teams win and lose together: the game is over when every team
    // still alive belongs to a single alliance.
    const aliveAlliances = new Set(aliveTeams.map((t) => t.allianceId));

    if (aliveAlliances.size <= 1) {
      this.phase = 'finished';
      this.winner = aliveTeams[0] || null;
      return true;
    }

    return false;
  }

  /** All teams in the winning alliance (empty when no winner). */
  getWinnerAllies(): Team[] {
    if (!this.winner) return [];
    return this.teams.filter((t) => t.allianceId === this.winner!.allianceId);
  }

  getWind(): number {
    return this.wind;
  }

  isCurrentPlayerTurn(teamId: string): boolean {
    return this.getCurrentTeam()?.id === teamId && this.phase === 'playing';
  }

  canFire(): boolean {
    return this.phase === 'playing';
  }

  /** True when the worm has fired this turn under the lock-after-fire rule. */
  isMovementLockedByFire(): boolean {
    return this.lockMovementAfterFire && this.shotsFiredThisTurn > 0;
  }

  canMove(): boolean {
    // GunBound rule: once you've fired this turn, you can no longer walk (you
    // can still aim/turn in place). syncedMovementLock carries the authority's
    // verdict to online clients so they don't mis-predict a rejected move.
    if (this.syncedMovementLock || this.isMovementLockedByFire()) return false;
    return this.phase === 'playing' || this.phase === 'retreat';
  }

  serialize(): GameStateData {
    return {
      teams: this.teams,
      currentTeamIndex: this.currentTeamIndex,
      turnTimeRemaining: this.turnTimeRemaining,
      retreatTimeRemaining: this.retreatTimeRemaining,
      roundNumber: this.roundNumber,
      wind: this.wind,
      phase: this.phase,
      winner: this.winner,
    };
  }
}
